import React from 'react';
import { formatters } from 'services';
import { ASSET_CLASSES } from 'state';
import type { Studio2Player } from '../../live/types';
import MiniChart from '../MiniChart';
import {
  pct,
  money,
  num,
  int,
  signClass,
  signedMoney,
  BAND_COLORS,
} from '../../helpers';

const StatLine: React.FC<{
  label: string;
  value: React.ReactNode;
  cls?: string;
}> = ({ label, value, cls }) => (
  <div className="studio2__statline">
    <span>{label}</span>
    <strong className={cls}>{value}</strong>
  </div>
);

const BehaviorPanel: React.FC<{ player: Studio2Player }> = ({ player }) => {
  const t = player.report.trades;
  const rpt = player.report.return_per_trade;
  // NOTE: on historical sessions `hold_period` (this card + "Avg hold by
  // segment") reads ~0 for every player. That's a BACKEND limitation, not a
  // wiring gap: `get_hold_period` derives everything from `Position.timedelta`,
  // which measures REAL wall-clock time (closed_at − created_at), not
  // scenario/market time — so in a fast historical replay every hold rounds to
  // ~0 days and `trades_percent` (needs ≥1 real day) is 0. The Position model
  // stores created_at_scenario_date / closed_at_scenario_date for exactly this,
  // but the computation doesn't use them. Fix is backend-side (use the scenario
  // dates + recompute reports); the overlay here copies the values faithfully.
  // See pm.api: reports/individual/get_hold_period.py and
  // financial_instruments/models_dir/position_models.py (timedelta).
  const h = player.report.hold_period;

  const payoffVals = [
    rpt.avg_profit,
    rpt.avg_loss,
    rpt.max_realized_profit,
    rpt.max_realized_loss,
  ];
  const holdVals = [
    h.hold_per_winners.avg,
    h.hold_per_losers.avg,
    h.hold_per_longs.avg,
    h.hold_per_shorts.avg,
  ];

  // Turnover by asset class — descending so the biggest desks sit on top of the
  // horizontal bar. Labels via the shared ASSET_CLASSES code→name map.
  const turnoverRows = [...player.asset_class_turnover].sort(
    (a, b) => b.reporting_turnover - a.reporting_turnover,
  );
  const turnoverCats = turnoverRows.map(
    r => ASSET_CLASSES[r.asset_class] ?? `Class ${r.asset_class}`,
  );
  const turnoverData = turnoverRows.map(r => r.reporting_turnover);

  return (
    <div className="studio2__behavior">
      <div className="studio2__card">
        <div className="studio2__card-head">
          <span className="studio2__card-title">Trades</span>
        </div>
        <div className="studio2__card-body">
          <StatLine label="Total trades" value={int(t.total)} />
          {/* Hidden per request (kept wired): <StatLine label="Daily average" value={num(t.daily_avg, 1)} /> */}
          <StatLine label="Daily max" value={int(t.daily_max)} />
          <StatLine
            label="Profitable %"
            value={pct(t.profitable_percent)}
            cls="s2-pos"
          />
          <StatLine
            label="Losing %"
            value={pct(t.losing_percent)}
            cls="s2-neg"
          />
          {/* Hidden per request (kept wired): <StatLine label="Intraday %" value={pct(t.intraday_percent)} /> */}
        </div>
      </div>

      <div className="studio2__card">
        <div className="studio2__card-head">
          <span className="studio2__card-title">Return per trade</span>
        </div>
        <div className="studio2__card-body">
          <StatLine
            label="Avg profit"
            value={signedMoney(rpt.avg_profit)}
            cls="s2-pos"
          />
          <StatLine
            label="Avg loss"
            value={signedMoney(rpt.avg_loss)}
            cls="s2-neg"
          />
          <StatLine
            label="Avg position PnL"
            value={signedMoney(rpt.position_pnl_avg)}
            cls={signClass(rpt.position_pnl_avg)}
          />
          <StatLine
            label="Max realized profit"
            value={signedMoney(rpt.max_realized_profit)}
            cls="s2-pos"
          />
          <StatLine
            label="Max realized loss"
            value={signedMoney(rpt.max_realized_loss)}
            cls="s2-neg"
          />
        </div>
      </div>

      <div className="studio2__card">
        <div className="studio2__card-head">
          <span className="studio2__card-title">Hold period</span>
        </div>
        <div className="studio2__card-body">
          <StatLine label="Average hold" value={`${num(h.avg_hold, 1)} days`} />
          <StatLine label="Min hold" value={`${int(h.min_hold)} days`} />
          <StatLine label="Max hold" value={`${int(h.max_hold)} days`} />
          {/* Hidden per request (kept wired): <StatLine label="Trades with holds %" value={pct(h.trades_percent)} /> */}
        </div>
      </div>

      <div className="studio2__card">
        <div className="studio2__card-head">
          <span className="studio2__card-title">Payoff profile</span>
        </div>
        <div className="studio2__card-body studio2__card-body--pad0">
          <MiniChart
            type="bar"
            series={[{ name: 'Per-trade PnL', data: payoffVals }]}
            categories={['Avg profit', 'Avg loss', 'Max profit', 'Max loss']}
            colors={payoffVals.map(v =>
              v >= 0 ? BAND_COLORS[0] : BAND_COLORS[3],
            )}
            distributed
            valueFormatter={(v: number) => formatters.million(v)}
          />
        </div>
      </div>

      <div className="studio2__card">
        <div className="studio2__card-head">
          <span className="studio2__card-title">Avg hold by segment</span>
        </div>
        <div className="studio2__card-body studio2__card-body--pad0">
          <MiniChart
            type="bar"
            series={[{ name: 'Avg hold', data: holdVals }]}
            categories={['Winners', 'Losers', 'Longs', 'Shorts']}
            colors={['#0f5fd6']}
            valueFormatter={(v: number) => `${formatters.commas1Digit(v)} days`}
          />
        </div>
      </div>

      <div className="studio2__behavior-col">
        {/* Outcome extremes — now shrinks to hug its stat lines */}
        <div className="studio2__card studio2__card--shrink">
          <div className="studio2__card-head">
            <span className="studio2__card-title">Outcome extremes</span>
          </div>
          <div className="studio2__card-body">
            <StatLine
              label="Max unrealized profit"
              value={signedMoney(rpt.max_unrealized_profit)}
              cls="s2-pos"
            />
            <StatLine
              label="Max unrealized loss"
              value={signedMoney(rpt.max_unrealized_loss)}
              cls="s2-neg"
            />
            <StatLine
              label="Min realized profit"
              value={money(rpt.min_realized_profit)}
            />
            <StatLine
              label="Min realized loss"
              value={money(rpt.min_realized_loss)}
            />
          </div>
        </div>

        {/* New tile — turnover breakdown fills the remaining height */}
        <div className="studio2__card studio2__card--grow">
          <div className="studio2__card-head">
            <span className="studio2__card-title">Turnover by asset class</span>
          </div>
          <div className="studio2__card-body studio2__card-body--pad0">
            {turnoverData.length ? (
              <MiniChart
                type="bar"
                horizontal
                series={[{ name: 'Turnover', data: turnoverData }]}
                categories={turnoverCats}
                colors={['#0f5fd6']}
                valueFormatter={(v: number) => formatters.million(v)}
                height="100%"
              />
            ) : (
              <div className="studio2__turnover-empty">
                {player.turnover_computed
                  ? 'No turnover yet'
                  : 'Turnover not computed for this session yet'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BehaviorPanel;
